import { useState } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { setTag } from "@/api/endpoints/tags";
import { postTrackingData } from "@/api/endpoints/track";
import CloseIcon from "@/assets/icons/close.svg";
import { useMentionCard } from "@/containers/MentionCard/context";
import { TagSelector } from "@/containers/MentionCard/partials/TagSelector";
import { useTags } from "@/contexts/TagContext";
import { EVENT } from "@/tracking_events";
import { cx } from "@/utils";
import AccessControl from "@components/AccessControl";
import Clickable from "@components/Clickable";
import Typography from "@components/Typography";

import styles from "./Tags.module.css";

const Tag = ({ className, onRemove, value }) => {
  return (
    <>
      <span className={cx(styles.tag, className)}>
        <Typography variant="caption" className={styles.text}>
          {value}
        </Typography>
        <AccessControl>
          <Clickable className={styles.close} onClick={onRemove}>
            <CloseIcon width={12} />
          </Clickable>
        </AccessControl>
      </span>
    </>
  );
};

const Tags = () => {
  const queryClient = useQueryClient();
  const { orgUuid } = useParams();
  const {
    mentions,
    tags: passedTags,
    title_mention_uuid,
    description_mention_uuid,
  } = useMentionCard();

  const [tags, setTags] = useState(passedTags || []);
  const { organizationTags, fetchOrganizationTags } = useTags() || {
    organizationTags: [],
  };

  const orgTags = organizationTags
    .filter((t) => t.name !== "favorited")
    .map((t) => {
      return {
        label: t.name,
        value: t.name,
        uuid: t.name,
      };
    });

  const onTagsChange = (newTags) => {
    let tagsToSave = [];
    if (newTags !== null) {
      tagsToSave = newTags.map((t) => t.value);
    }

    setTags(tagsToSave);
    const mentionUuids = [
      ...mentions.map((m) => m.public_id),
      title_mention_uuid,
      description_mention_uuid,
    ].filter(Boolean);

    setTag({ tags: tagsToSave, mentionUuids: mentionUuids })
      .then(() => {
        postTrackingData(EVENT.TAGGED_MENTION, {
          uuid: mentionUuids,
          tags: tagsToSave,
        });
        fetchOrganizationTags();
        if (orgUuid) {
          queryClient.invalidateQueries({
            queryKey: [orgUuid, "tags"],
          });
        }
      })
      .catch((error) => {
        throw error;
      });
  };

  const tagsChosen = tags
    .filter((t) => t !== "favorited")
    .map((t) => {
      return {
        label: t,
        value: t,
        uuid: t,
      };
    });

  return (
    <>
      {tagsChosen.map((tag, i) => (
        <Tag
          value={tag.value}
          key={`single-tag-${i}`}
          onRemove={() =>
            onTagsChange(tagsChosen.filter((t) => t.value !== tag.value))
          }
        />
      ))}
      <AccessControl>
        <TagSelector
          value={tagsChosen}
          onChange={onTagsChange}
          options={orgTags}
        />
      </AccessControl>
    </>
  );
};
export default Tags;
